import { BoExternalSavingsFundsRequestDetails } from '@Common/types/bo_external/savings_funds_requests';
import { CoCardTopUpsStatus } from '@Common/types/funding/deposit';

import { KeysOfUnion } from '@sh4res/bo-common/utils/keys_of_union';

export const b2bSavingsFundsRequestsTranslations = {
  fields: {
    id: 'Fund Request ID',
    b2b_company_id: 'Company ID',
    b2b_withdrawal_id: 'B2B Withdrawal ID',
    company_name: 'Company name',
    number_of_eligible_employees: 'Number of eligible employees',
    date: 'Date',
    total_gross_amount: 'Total gross amount',
    total_net_amount: 'Total net amount to be paid',
    total_contribution_amount: 'Total social contribution amount',
    created_at: 'Created',
    updated_at: 'Updated',
    status: 'Status',
    execution_error: 'Execution Error',
    type: 'Type',
    payment_reference: 'Payment reference',
    due_date: 'Due date',
    co_payment_status: 'Payment status',
    campaign_id: 'Linked Operation ID',
    campaign_type: 'Operation Type',
    currency: 'Currency',
    ts_user_total_nb_eligible: 'Number of eligible employees',
    ts_user_total_gross_amount: 'Gross amount (employees)',
    ts_user_total_contribution_amount: '9.7% Social contribution amount (employees)',
    ts_user_total_csg_tax_amount: '9.2% CSG amount (employees)',
    ts_user_total_crds_tax_amount: '0.5% CRDS amount (employees)',
    ts_user_total_net_amount: 'Net amount to be paid (employees)',
    tns_user_total_nb_eligible: 'Number of eligible self employed',
    tns_user_total_gross_amount: 'Gross amount (self employed)',
    tns_user_total_contribution_amount: '9.7% Social contribution amount (self employed)',
    tns_user_total_csg_tax_amount: '9.2% CSG amount (self employed)',
    tns_user_total_crds_tax_amount: '0.5% CRDS amount (self employed)',
    tns_user_total_net_amount: 'Net amount to be paid (self employed)',
    campaign_operation_summary: 'Operation summary document',
    fund_request_document: 'Fund request document',
    is_marked_as_completed: 'Is marked as completed',
    is_marked_as_paid: 'Is marked as paid',
    is_marked_as_cancelled: 'Is marked as cancelled',
    is_sepa_direct_debit_retryable: 'Can retry SEPA direct debit',
  } satisfies Record<
    KeysOfUnion<
      BoExternalSavingsFundsRequestDetails & {
        campaign_operation_summary: string;
        fund_request_document: string;
        execution_error: string;
        due_date: string;
      }
    >,
    string
  >,
  employees_breakdown: {
    title: 'Employees Breakdown',
  },
  contractors_breakdown: {
    title: 'Self employed Breakdown',
  },
  employees: {
    section_title: 'Breakdown per eligible employee',
    totals: {
      number_of_eligible_employees: 'Number of eligible employees',
      total_gross_amount: 'Total gross amount',
      total_contribution_amount: '9.7% Social contribution amount',
      total_csg_tax_amount: '9.2% CSG amount',
      total_crds_tax_amount: '0.5% CRDS amount',
      total_net_amount: 'Total net amount to be paid',
    },
    fields: {
      employee_id: 'Employee ID',
      firstname: 'First name',
      lastname: 'Last name',
      identifier: 'Identifier',
      registration_number: 'Registration number',
      social_status: 'Social status',
      gross_amount: 'Gross amount',
      contribution_amount: 'Social contribution amount',
      csg_tax_amount: 'CSG amount',
      crds_tax_amount: 'CRDS amount',
      net_amount: 'Net amount',
    },
  },
  choices: {
    status: {
      check_employer_balance: 'Check employer balance',
      promote_orders: 'Promote orders',
      funds_request_paid: 'Paid',
      funds_request_completed: 'Completed',
      check_employer_balance_failed: 'Check employer balance Failed',
      promote_orders_failed: 'Promote orders Failed',
      funds_request_paid_failed: 'Paid Failed',
      funds_request_completed_failed: 'Completed Failed',
      finished: 'Finished',
      finished_failed: 'Finished Failed',
      cancelled: 'Cancelled',
      cancelled_failed: 'Cancelled Failed',
      pending: 'Pending',
    } satisfies Record<
      | Exclude<
          NonNullable<BoExternalSavingsFundsRequestDetails['status']>,
          'funds_request_pending'
        >
      | 'pending',
      string
    >,
    recon_status: {
      balanced: 'Balanced',
      over_paid: 'Over-paid',
      under_paid: 'Under-paid',
    },
    co_payment_status: {
      initiated: 'Initiated',
      pending: 'Pending',
      captured: 'Captured',
      rejected: 'Rejected',
      settled: 'Settled',
      authorization_declined: 'Authorization declined',
      shares_cancelled: 'Cancelled',
      pending_declined: 'Pending declined',
      card_verified: 'Card verified',
      card_verification_declined: 'Card verification declined',
      returned: 'Returned',
      refunded: 'Refunded',
      partially_refunded: 'Partially refunded',
    } satisfies Record<CoCardTopUpsStatus, string>,
    type: {
      b2b_management_fee: 'Management Fees',
      b2b_custody_fee: 'Custody Fees',
      matching_contribution: 'Matching contribution',
      campaign: 'Operation',
      ccb_withdrawal: 'CCB Withdrawal',
      capital_increase: 'Capital Increase',
    } satisfies Record<BoExternalSavingsFundsRequestDetails['type'], string>,
    treasury_color: {
      green: 'Balanced',
      orange: 'Balanced (pending)',
      red: 'Unbalanced',
    },
  },
  orders: {
    fields: {
      id: 'Order ID',
      total_gross_amount: 'Gross amount',
      user_currency_amount: 'Net amount',
      tax_amount: 'Social contribution amount',
      matched_contribution_order_id: 'Linked Order ID',
      entry_fee_amount: 'Entry fee amount',
      entry_fee_pct: 'Entry fee %',
      instrument_id: 'Instrument ID',
      isin: 'ISIN',
      status: 'Order status',
    },
    section_title: 'Breakdown per eligible orders',
    subtitle: 'The export includes both Libre and Piloté/Profilé investments.',
    export: {
      button: 'Export all',
    },
  },
  payouts: {
    fields: {
      id: 'Payout ID',
      total_gross_amount: 'Gross amount',
      user_currency_amount: 'Net amount',
      tax_amount: 'Social contribution amount',
      status: 'Payout status',
    },
    section_title: 'Payouts',
  },
  fully_managed_plan_contributions: {
    fields: {
      id: 'Contribution ID',
      total_gross_amount: 'Gross amount',
      user_currency_amount: 'Net amount',
      tax_amount: 'Social contribution amount',
      status: 'Contribution status',
    },
    section_title: 'Fully managed plan contributions',
  },
  payments: {
    button: 'Pay',
    title: 'Initiate a payment',
    step_payment: 'Payment',
    step_confirmation: 'Confirmation',
    amount_paid: 'Amount paid',
    beneficiary_details: 'Beneficiary details',
    fields: {
      name: 'Beneficiary name',
      acc_iban: 'IBAN',
      acc_bic: 'BIC/SWIFT',
      reference: 'Payment reference',
    },
    confirmation_title: 'Confirmation',
    confirmation:
      'I confirm I have initiated the payment to the following beneficiary and added the Payment reference',
    use_beneficiary_details: 'Use the beneficiary details below',
    important_information_title: 'Important information',
    important_information_body_1:
      "Initiate a payment from an account in your company's name. Transfers from third party accounts will be rejected.",
    important_information_body_2: 'Only Euro transfers are accepted.',
    important_information_body_3: 'Transfers generally takes 1 to 2 business days.',
    success: 'Action sent',
    failure: 'Error sending action: %{error}',
    completed: 'Mark as Completed',
    mark_as_paid: 'Mark as Paid',
    cant_show_bank_details:
      'Beneficiary details are not available, please contact your administrator',
  },
};
